import React, { useRef, useState, useEffect } from "react";

import { Play, Download, Expand, Video } from "lucide-react";

const formatarDuracao = (segundos) => {
	if (!Number.isFinite(segundos)) return null;

	const total = Math.round(segundos);
	const minutos = Math.floor(total / 60);
	const resto = total % 60;

	return `${minutos}:${String(resto).padStart(2, "0")}`;
};

const extensaoDe = (url) => {
	try {
		const caminho = new URL(url, window.location.origin).pathname;
		const match = caminho.match(/\.[A-Za-z0-9]{2,6}$/);
		return match ? match[0].toLowerCase() : "";
	} catch {
		return "";
	}
};

const VideoNaConversa = ({ url }) => {
	const videoRef = useRef(null);
	const videoFullscreenRef = useRef(null);
	const playCountRef = useRef(0);

	const [duracao, setDuracao] = useState(null);
	const [tocando, setTocando] = useState(false);
	const [tamanho, setTamanho] = useState({ w: 0, h: 0 });
	const [carregando, setCarregando] = useState(false);
	const [abertoFullscreen, setAbertoFullscreen] = useState(false);
	const [erroFallbackImg, setErroFallbackImg] = useState(false);
	const [usarCrossOrigin, setUsarCrossOrigin] = useState(false);
	const [mudo, setMudo] = useState(false);
	// IMPORTANTÍSSIMO: erroFinal SÓ é verdadeiro se o usuário CLICOU em
	// PLAY DUAS VEZES, os dois deram erro SRC_NOT_SUPPORTED e metadata nunca
	// carregou. onError da tag NÃO dispara esse flag — ele conta erros
	// temporários (range 206, redirect, moov no fim do MP4) que acontecem
	// em vídeos 100% válidos do WhatsApp e acabavam caindo no fallback
	// "Abrir em nova aba" sem necessidade.
	const [erroFinal, setErroFinal] = useState(false);

	const extensao = extensaoDe(url);
	const ehImagemAnimada = [".gif", ".webp", ".apng", ".avif"].includes(extensao);
	const ehVideoReal = [".mp4", ".webm", ".mov", ".m4v", ".ogv", ".3gp", ".mkv", ".avi"].includes(extensao);

	// DEFAULT widescreen 16:9 horizontal (igual WhatsApp Web para vídeos
	// antes do metadata carregar). SÓ troca para story/quadrado DEPOIS que
	// onLoadedMetadata provou proporção real.
	const metadataCarregou = tamanho.w > 0 && tamanho.h > 0;
	const proporcao = metadataCarregou ? tamanho.h / tamanho.w : 9 / 16;
	const ehStory = proporcao >= 1.4;
	const ehQuadrado = metadataCarregou && proporcao >= 0.9 && proporcao < 1.4;

	useEffect(() => {
		if (!abertoFullscreen) return undefined;
		const aoTeclarEsc = (e) => {
			if (e.key === "Escape") setAbertoFullscreen(false);
		};
		document.addEventListener("keydown", aoTeclarEsc);
		document.body.style.overflow = "hidden";
		const grande = videoFullscreenRef.current;
		const pequeno = videoRef.current;
		return () => {
			document.removeEventListener("keydown", aoTeclarEsc);
			document.body.style.overflow = "";
			if (grande && pequeno) {
				try {
					pequeno.currentTime = grande.currentTime;
				} catch {
					/* no-op */
				}
			}
		};
	}, [abertoFullscreen]);

	const tocar = async () => {
		const el = videoRef.current;
		if (!el) return;

		playCountRef.current += 1;
		setCarregando(true);
		setTocando(true);

		try {
			if (el.readyState < 2) {
				// Garante o load() mesmo se preload=metadata ou não começou
				// baixar ainda. Espera readyState trocar por ATÉ 5 segundos.
				el.load();
				let esperou = 0;
				while (el.readyState < 2 && esperou < 5000) {
					await new Promise((r) => setTimeout(r, 150));
					esperou += 150;
				}
			}

			// Tenta 1: play normal
			try {
				await el.play();
				return;
			} catch {
				/* ignora, segue para muted abaixo */
			}

			// Tenta 2: play muted (Autoplay Policy bloqueia com som)
			try {
				el.muted = true;
				setMudo(true);
				await el.play();
				return;
			} catch {
				/* ignora, segue para crossOrigin */
			}

			// Tenta 3: se ainda não usou crossOrigin, habilita e retenta
			if (!usarCrossOrigin) {
				setUsarCrossOrigin(true);
				// Tenta novamente após remount ~250ms
				setTimeout(() => tocar(), 300);
				return;
			}
			// Tenta 4: se já usava crossOrigin e não é mudo, tenta mudo.
			if (!mudo) {
				setMudo(true);
				setTimeout(() => tocar(), 200);
				return;
			}
		} finally {
			setTimeout(() => setCarregando(false), 400);
		}

		// ── ÚNICO LUGAR que seta erroFinal ─────────────────────────────
		// Vídeo só é considerado "não suportado / corrompido" se:
		//   a) usuário clicou 2+ vezes em play
		//   b) todas as 4 tentativas acima falharam
		//   c) onLoadedMetadata NUNCA disparou (duração null + tamanho 0,0)
		//      → se metadata carregou, o arquivo é 100% válido; erro é
		//      codec/browser e o usuário pode Abrir em nova aba.
		if (playCountRef.current >= 2 && duracao === null && !metadataCarregou) {
			setTocando(false);
			setErroFinal(true);
		} else {
			// Não é corrompido. Só volta o tocando para false e deixa o
			// overlay de play aparecer de novo — não esconde o player.
			setTocando(false);
		}
	};

	// onError: NÃO GERA FALLBACK. Só liga crossOrigin/mudo se for o
	// primeiro erro. Motivo: esse evento dispara por MUITAS coisas
	// temporárias (range 206, redirect, moov no fim do arquivo MP4
	// enviado pelo iPhone WhatsApp que grava ftyp no fim) em arquivos
	// 100% válidos. Se fosse por "2 erros = corrompido" todo vídeo do
	// WhatsApp caía no fallback — exatamente o bug do usuário.
	const tratarErro = () => {
		if (!usarCrossOrigin) setUsarCrossOrigin(true);
		else if (!mudo) setMudo(true);
		// E NÃO FAZ NADA MAIS. Não desiste. Não chama erroFinal.
	};

	const aoCarregarMetadata = (e) => {
		const el = e.currentTarget;
		setDuracao(el.duration);
		const w = el.videoWidth || 0;
		const h = el.videoHeight || 0;
		if (w > 0 && h > 0) setTamanho({ w, h });
	};

	const botoesSuperiores = (labelExpandir) => (
		<div className="absolute right-1.5 top-1.5 flex gap-1 z-10">
			<button
				type="button"
				onClick={() => setAbertoFullscreen(true)}
				title="Expandir"
				aria-label={labelExpandir}
				className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-90 hover:bg-black/80"
			>
				<Expand className="h-3.5 w-3.5" />
			</button>
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				title="Baixar mídia"
				aria-label="Baixar mídia"
				className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-90 hover:bg-black/80"
			>
				<Download className="h-3.5 w-3.5" />
			</a>
		</div>
	);

	// Tamanhos balão IGUAIS WhatsApp Web.
	const largura = ehStory ? 180 : ehQuadrado ? 220 : 260;
	const alturaCalc = Math.round(largura * proporcao);
	const alturaMax = 320;
	const alturaFinal = Math.min(alturaCalc, alturaMax);

	const classesFullscreen = ehStory
		? "max-h-[92vh] w-auto h-auto"
		: ehQuadrado
		? "max-w-[70vw] max-h-[92vh] w-auto h-auto"
		: "max-w-[92vw] max-h-[92vh] w-auto h-auto";

	// Fallback GIF / imagens animadas (marcadas como "video" erroneamente)
	if (erroFinal && ehImagemAnimada) {
		if (erroFallbackImg) {
			return (
				<div className="relative mb-1 max-w-[260px]">
					<div className="flex min-h-[120px] max-w-[260px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20 p-4 text-xs text-muted-foreground">
						<span>Não foi possível carregar a mídia</span>
						<a
							href={url}
							target="_blank"
							rel="noreferrer noopener"
							className="underline-offset-2 hover:underline"
						>
							Abrir em nova aba
						</a>
					</div>
					{botoesSuperiores("Expandir mídia")}
				</div>
			);
		}
		return (
			<div className="relative mb-1 max-w-[260px]">
				<img
					src={url}
					alt="Mídia"
					loading="lazy"
					decoding="async"
					className="max-w-[260px] rounded-lg bg-muted object-contain"
					onClick={() => setAbertoFullscreen(true)}
					style={{ cursor: "zoom-in" }}
					onError={() => setErroFallbackImg(true)}
				/>
				{botoesSuperiores("Expandir mídia")}
				{abertoFullscreen && (
					<div
						className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
						onClick={() => setAbertoFullscreen(false)}
						role="dialog"
						aria-modal="true"
						aria-label="Pré-visualização de mídia"
					>
						<img
							src={url}
							alt="Mídia"
							className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
							onClick={(e) => e.stopPropagation()}
						/>
					</div>
				)}
			</div>
		);
	}

	// Fallback REALMENTE corrompido / codec não suportado.
	// SÓ cai aqui após 2 cliques em play sem metadata nenhuma.
	if (erroFinal && ehVideoReal) {
		return (
			<div className="relative mb-1" style={{ width: largura }}>
				<div
					className="flex flex-col items-center justify-center gap-2 rounded-lg bg-black p-4 text-xs text-white/90"
					style={{ width: largura, minHeight: 160 }}
				>
					<div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
						<Video className="h-5 w-5" />
					</div>
					<span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">
						Vídeo
					</span>
					<span className="text-center leading-snug text-white/80">
						Formato não suportado no navegador
					</span>
					<a
						href={url}
						target="_blank"
						rel="noreferrer noopener"
						className="mt-1 inline-flex items-center gap-1 rounded-md bg-white/15 px-3 py-1.5 text-white underline-offset-2 hover:bg-white/25 hover:underline"
					>
						<Expand className="h-3.5 w-3.5" />
						Abrir em nova aba
					</a>
				</div>
				{botoesSuperiores("Abrir vídeo em nova janela")}
			</div>
		);
	}

	return (
		<div className="relative mb-1" style={{ width: largura }}>
			<video
				// key trocada SOMENTE quando crossOrigin muda. Mudo sozinho
				// não precisa de remount (atualiza o atributo via setMudo +
				// el.muted direto no tocar())
				key={usarCrossOrigin ? "v-cors" : "v-same"}
				ref={videoRef}
				src={url}
				preload="auto"
				controls
				playsInline
				muted={mudo}
				{...(usarCrossOrigin ? { crossOrigin: "anonymous" } : {})}
				onLoadedMetadata={aoCarregarMetadata}
				onPlay={() => setTocando(true)}
				onPause={() => setTocando(false)}
				onEnded={() => setTocando(false)}
				onWaiting={() => setCarregando(true)}
				onPlaying={() => setCarregando(false)}
				onCanPlay={() => setCarregando(false)}
				onError={tratarErro}
				className="rounded-lg bg-black object-contain"
				style={{
					width: largura,
					maxHeight: 320,
					height: ehStory ? alturaFinal : "auto",
					aspectRatio: `${largura} / ${Math.max(1, Math.round(largura * proporcao))}`,
					display: "block",
				}}
			/>

			{/* Overlay capa: só aparece quando NÃO está tocando. IGUAL
			    WhatsApp Web: botão Play branco centralizado 56px */}
			{!tocando && (
				<button
					type="button"
					onClick={tocar}
					aria-label={carregando ? "Carregando vídeo..." : "Reproduzir vídeo"}
					className={`absolute inset-0 flex items-center justify-center rounded-lg transition ${
						carregando ? "cursor-wait" : ""
					}`}
				>
					<span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white shadow-[0_0_24px_rgba(0,0,0,0.35)] hover:bg-black/70">
						{carregando ? (
							<span className="h-6 w-6 animate-spin rounded-full border-2 border-white/40 border-t-white" />
						) : (
							<Play className="ml-0.5 h-7 w-7 fill-current" />
						)}
					</span>
				</button>
			)}

			{!tocando && duracao !== null && (
				<span className="pointer-events-none absolute bottom-1.5 left-1.5 z-10 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white">
					{formatarDuracao(duracao)}
				</span>
			)}

			{botoesSuperiores("Expandir vídeo")}

			{abertoFullscreen && (
				<div
					className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
					onClick={() => {
						setAbertoFullscreen(false);
						videoFullscreenRef.current?.pause();
					}}
					role="dialog"
					aria-modal="true"
					aria-label="Pré-visualização de vídeo"
				>
					<video
						ref={videoFullscreenRef}
						src={url}
						controls
						playsInline
						autoPlay
						preload="auto"
						className={`${classesFullscreen} rounded-lg bg-black shadow-2xl`}
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			)}
		</div>
	);
};

export default VideoNaConversa;
